import { useState } from "react";
import { ApiError, type DaysLayout } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";

/**
 * KEY-767: how this dial lays out its coming days, for everyone on it — each day split into its times of day
 * (Morning, Afternoon, Evening, Night) with the ones nobody needs hidden, or each day whole. A hidden time of day's
 * appointments fold into the one before it, so nothing disappears. Changing it reshapes the dial, so it reloads.
 */
export function DaysLayoutCard({ instanceId, layout, onChanged }: { instanceId: string; layout: DaysLayout; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (allDay: boolean, hiddenSlots: string[]) => {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.setDaysLayout(token, instanceId, allDay, hiddenSlots);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "Couldn't change that just now.");
    } finally {
      setBusy(false);
    }
  };

  const hidden = new Set(layout.hiddenSlots);
  const shown = layout.slots.filter((s) => !hidden.has(s.key));

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>The coming days</div>
      <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
        Each day is split into times of day, and each appointment sits in the part of the day it happens in. Hide the
        ones you don't need — their appointments move into the one before — or show each day whole.
      </p>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 9, marginTop: 14, cursor: busy ? "wait" : "pointer" }}>
        <input type="checkbox" checked={layout.allDay} disabled={busy} onChange={(e) => void save(e.target.checked, layout.hiddenSlots)} />
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Show each day whole</span>
      </label>
      {!layout.allDay && layout.slots.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 12 }}>
          {layout.slots.map((slot) => (
            <label key={slot.key} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13.5, color: "var(--text)", cursor: busy ? "wait" : "pointer" }}>
              <input
                type="checkbox"
                checked={!hidden.has(slot.key)}
                // The last time of day showing can't be hidden — that is what "Show each day whole" is for.
                disabled={busy || (!hidden.has(slot.key) && shown.length === 1)}
                onChange={(e) => void save(false, e.target.checked ? layout.hiddenSlots.filter((k) => k !== slot.key) : [...layout.hiddenSlots, slot.key])}
              />
              {slot.name} <span style={{ color: "var(--text-faint)" }}>from {slot.startsAt}</span>
            </label>
          ))}
        </div>
      )}
      {error && <p role="status" style={{ margin: "10px 0 0", fontSize: 13, color: "var(--text-muted)" }}>{error}</p>}
    </div>
  );
}
